import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Site, SiteStatus } from '@/contracts/site.contract'
import { siteApi } from '@/services/site.api'

/**
 * 站台狀態。
 *
 * 這是一個白標平台：同一份前端會被多個站使用，各自有品牌與營運狀態。
 * 前端不決定自己是哪個站 —— 那是後端依域名判斷的（見
 * `api/src/contracts/site.contract.ts`），這裡只是把答案拿回來。
 *
 * 存在的理由是**維護畫面**：後端維護中時所有 API 都回 503，
 * 沒有這支的話使用者只會看到一堆載入失敗，卻不知道為什麼。
 */
export const useSiteStore = defineStore('site', () => {
  const site = ref<Site | null>(null)
  /** 還沒問到答案。首屏在這個狀態下不該閃一下維護畫面 */
  const isLoading = ref(true)

  /**
   * 站台不能用時的狀態，可用時是 `null`。
   *
   * 回「狀態」而不是一個布林：畫面要依維護／停用顯示不同的文案，
   * 有布林還要另外再取一次狀態，兩個地方就有機會不一致。
   *
   * `site` 還是 null 時回 `null` —— **問不到就當作可用**。
   * 反過來設計的話，後端只是暫時抽風就會讓每個使用者看到維護畫面，
   * 那比讓他看到正常畫面然後某個請求失敗更糟。
   */
  const blockedStatus = computed<Exclude<SiteStatus, 'active'> | null>(() => {
    const status = site.value?.status
    return status === 'maintenance' || status === 'disabled' ? status : null
  })

  async function load(): Promise<void> {
    isLoading.value = true

    try {
      const result = await siteApi.get()
      site.value = result.site
    } catch {
      // 連站台資訊都拿不到，可能是網路斷了、也可能是部署設定錯誤。
      // 兩種都不該擋住畫面 —— 讓使用者進去，後續的請求自己會報錯
      site.value = null
    } finally {
      isLoading.value = false
    }
  }

  return { site, isLoading, blockedStatus, load }
})
